<template>
  <div class="go-pro-funnel">
    <div class="go-pro-funnel__logo-container">
      <img src="@/assets/images/icons/logo/text-logo-light.svg" alt="ProGuides Logo" class="go-pro-funnel__logo">
    </div>
    <div class="go-pro-funnel__panel">
      <div class="go-pro-funnel__image-panel">
        <img :src="playerImage" alt="Go Pro Player Image" class="go-pro-funnel__player-image">
      </div>
      <div v-if="membershipSubscriptions.length > 0" class="go-pro-funnel__benefit-panel">
        <h1 class="go-pro-funnel__header">You're already a premium member!</h1>
        <div class="go-pro-funnel__benefits-container">
          <p class="go-pro-funnel__benefits-intro">
            You have access to ProGuides premium features! Including:
          </p>
          <TheMembershipBenefitsList :benefits="userMembershipBenefits" />
        </div>
        <div class="go-pro-funnel__button-row">
          <router-link :to="{ name: ROUTER_NAMES.DASHBOARD }" class="go-pro-funnel__cta-btn">
            Access your benefits
          </router-link>
        </div>
        <TheAimlabsDiscoveryDisclaimer v-if="membershipIncludesDiscoveryAccess" />
      </div>
      <div v-else class="go-pro-funnel__benefit-panel">
        <h1 class="go-pro-funnel__header">Go pro and receive these great benefits:</h1>
        <div class="go-pro-funnel__benefits-container">
          <TheMembershipBenefitsList :benefits="productBenefits" />
        </div>
        <div class="go-pro-funnel__price-container">
          <p class="go-pro-funnel__for-only">For only</p>
          <img class="go-pro-funnel__marked-down-icon" src="@/assets/images/go-pro-funnel/marked-down.svg" alt="$12.99 original price">
          <div class="go-pro-funnel__price-row">
            <div class="go-pro-funnel__dollar-sign-container">
              <div class="go-pro-funnel__dollar-sign">$</div>
              <div class="go-pro-funnel__dollar-sign-spacer"></div>
            </div>
            <div class="go-pro-funnel__dollar-amount">{{ priceDollarAmount }}</div>
            <div class="go-pro-funnel__decimal-month">
              <div class="go-pro-funnel__decimal-amount">.{{ priceCentsAmount }}</div>
              <div class="go-pro-funnel__month">/month</div>
            </div>
          </div>
        </div>
        <div class="go-pro-funnel__button-row">
          <button @click="handleSubscribeButtonClick" class="go-pro-funnel__cta-btn">{{ productCTA }}</button>
        </div>
        <TheAimlabsDiscoveryDisclaimer />
      </div>
    </div>
    <div class="go-pro-funnel__overlay" :class="{ dimmed: paymentModalShow }"></div>
    <transition name="fade">
      <div
        v-if="paymentModalShow"
        v-scroll-lock="true"
        class="payment-modal-container"
      >
        <PaymentModal
          :productType="productType"
          :btnCopy="btnCopy"
          :maxNumPromoCodes="1"
          @closePaymentModal="setPaymentModalShow(false)"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import PaymentModal from "@/components/payment_v2/PaymentModal.vue"
import TheAimlabsDiscoveryDisclaimer from "@/components/store/subscription/TheAimlabsDiscoveryDisclaimer.vue"
import TheMembershipBenefitsList from "@/components/store/subscription/benefits-panel/TheMembershipBenefitsList.vue"
import { PRODUCT_TYPE } from "@/constants/payment"
import { ROUTER_NAMES } from "@/constants/router.constants"
import debounce from "lodash/debounce"
import { mapActions, mapGetters, mapMutations, mapState } from "vuex"

const BENEFITS = {
  DISCOVERY: "Unlimited access to Aimlabs Discovery, the first AI-powered task engine in gaming. *",
  COURSES: "Unlimited access to dozens of master classes taught by the greatest athletes in esports.",
  BOOTCAMPS: "Unlimited access to 14 & 30-day Bootcamps from top pros like ImperialHal, Doublelift, and Pengu.",
}

export default {
  name: "GoProFunnel",
  components: {
    PaymentModal,
    TheAimlabsDiscoveryDisclaimer,
    TheMembershipBenefitsList,
  },
  data: function () {
    return {
      // TODO: Use perks from listed Subscription Product fetched from API
      productBenefits: [
        BENEFITS.DISCOVERY,
        BENEFITS.COURSES,
        BENEFITS.BOOTCAMPS,
      ].map((benefitText) => ({ benefitText })),
      playerImage: "",
    }
  },
  computed: {
    ...mapState("Store", ["paymentModalShow"]),
    ...mapGetters("User", ["getUser"]),
    ...mapGetters("User/Subscriptions", [
      "coachingDiscountPercentage",
      "membershipIncludesDiscoveryAccess",
      "membershipIncludesPremiumContentAccess",
      "membershipSubscriptions",
    ]),
    ...mapGetters("Store", ["subscriptionOptions"]),
    product () {
      return this.subscriptionOptions[this.subscriptionOptions.length - 1]
        .product
    },
    price () {
      return "" + this.product.billing_details.regular.price.amount
    },
    priceDollarAmount () {
      return this.price.split(".")[0]
    },
    priceCentsAmount () {
      return this.price.split(".")[1]
    },
    btnCopy () {
      return "JOIN PRO NOW"
    },
    productCTA () {
      return "Subscribe now"
    },
    productType () {
      return PRODUCT_TYPE.MEMBERSHIP_SUBSCRIPTION
    },
    userMembershipBenefits () {
      const benefits = []

      if (this.membershipIncludesDiscoveryAccess) {
        benefits.push(BENEFITS.DISCOVERY)
      }

      if (this.membershipIncludesPremiumContentAccess) {
        benefits.push(...[BENEFITS.COURSES, BENEFITS.BOOTCAMPS])
      }

      return benefits.map((benefitText) => ({ benefitText }))
    },
  },
  methods: {
    ...mapActions("User/Subscriptions", ["loadUserSubscriptions"]),
    ...mapMutations("Store", ["setPaymentModalShow"]),
    handleSubscribeButtonClick () {
      if (this.getUser) {
        this.setPaymentModalShow(true)
      } else {
        this.$router.push("/register?from=%2Fstore%2Fsubscriptions")
      }
    },
    updateImageSrc () {
      if (window.innerWidth <= 768) {
        this.playerImage = require("@/assets/images/go-pro-funnel/go-pro-player-mobile.png")
      } else {
        this.playerImage = require("@/assets/images/go-pro-funnel/go-pro-player.jpg")
      }
    },
  },
  created () {
    this.ROUTER_NAMES = ROUTER_NAMES
    this.loadUserSubscriptions()
    this.updateImageSrc()
  },
  mounted () {
    this.debouncedUpdateImageSrc = debounce(this.updateImageSrc, 100)
    window.addEventListener("resize", this.debouncedUpdateImageSrc)
  },
  beforeDestroy () {
    window.removeEventListener("resize", this.debouncedUpdateImageSrc)
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/style/_variables.scss";
@import "@/assets/style/_mixins.scss";

.go-pro-funnel__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.dimmed {
  background-color: rgba(0, 0, 0, 0.6);
}

.go-pro-funnel {
  background-color: black;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
}

.go-pro-funnel__logo-container {
  margin-top: 2rem;
  text-align: center;
}

.go-pro-funnel__panel {
  background-color: white;
  max-width: 1200px;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 1.6rem;
  overflow: hidden;
  margin: 4rem 0;

  @include min-tablet {
    flex-direction: row;
  }
}

.go-pro-funnel__image-panel {
  flex: 1;
}

.go-pro-funnel__player-image {
  width: 100%;
  height: auto;
  object-fit: contain;

  @include min-tablet {
    height: 100%;
    object-fit: cover;
  }
}

.go-pro-funnel__benefit-panel {
  flex: 1;
  font-family: 'Proxima Nova', sans-serif;
  padding: 1.6rem 4rem;
  display: flex;
  flex-direction: column;

  @include min-tablet {
    padding: 8rem 7rem 8rem 5rem;
  }
}

.go-pro-funnel__header {
  font-size: 3.2rem;
  font-weight: 700;
  margin-bottom: 1.6rem;
  color: #3B3B3B;
  text-align: left;

  @include min-tablet {
    font-size: 3.6rem;
    margin-bottom: 2.8rem;
  }
}

.go-pro-funnel__benefits-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 2rem;
}

.go-pro-funnel__benefits-intro {
  color: $text-color-primary;
  font-size: 1.6rem;
  font-weight: 400;
  text-align: left;

  @include min-tablet {
    font-size: 2rem;
  }
}

.go-pro-funnel__price-container {
  margin-bottom: 2rem;
}

.go-pro-funnel__for-only {
  font-size: 1.8rem;
  font-weight: 600;
  text-align: center;
  line-height: 2.2rem;
  color: black;
  margin-bottom: 2rem;

  @include min-tablet {
    font-size: 2rem;
    line-height: 2.4rem;
  }
}

.go-pro-funnel__marked-down-icon {
  height: 2.6rem;
  position: relative;

  @include min-tablet {
    height: 3rem;
  }
}

.go-pro-funnel__price-row {
  margin-top: -1.4rem;
  display: flex;
  align-items: stretch;
  justify-content: center;

  @include min-tablet {
    margin-top: -2rem;
    font-size: 2rem;
    line-height: 2.4rem;
  }
}

.go-pro-funnel__dollar-sign-container {
  display: flex;
  flex-direction: column;
}

.go-pro-funnel__dollar-sign {
  flex: 1;
  font-size: 1.8rem;
  font-weight: 800;
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;

  @include min-tablet {
    font-size: 2rem;
    line-height: 2.4rem;
  }
}

.go-pro-funnel__dollar-sign-spacer {
  flex: 1;
}

.go-pro-funnel__dollar-amount {
  font-size: 8.4rem;
  line-height: 10.2rem;
  font-weight: 800;
  color: black;
  margin-left: 0.2rem;

  @include min-tablet {
    font-size: 12.8rem;
    line-height: 15.6rem;
  }
}

.go-pro-funnel__decimal-month {
  display: flex;
  flex-direction: column;
}

.go-pro-funnel__decimal-amount {
  flex: 1;
  font-size: 3rem;
  line-height: 3.6rem;
  font-weight: 800;
  color: black;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @include min-tablet {
    font-size: 4rem;
    line-height: 4.8rem;
  }
}

.go-pro-funnel__month {
  flex: 1;
  font-size: 1.2rem;
  font-weight: 400;
  color: black;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.go-pro-funnel__cta-btn {
  display: inline-block;
  width: 100%;
  background-color: #47D8D7;
  font-size: 1.6rem;
  line-height: 1.9rem;
  font-weight: 600;
  color: #201D1C;
  text-transform: uppercase;
  padding: 2rem 0;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  text-align: center;
  margin-bottom: 2rem;

  @include min-tablet {
    width: auto;
    padding: 2rem 10rem;
    font-size: 1.8rem;
    line-height: 2.2rem;
  }
}

.payment-modal-container {
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  z-index: 25;
  font-family: $font-primary;
  color: $dark-gray;
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;

  &::before {
    content: '';
    background-color: rgba(black, 0.2);
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

</style>



// WEBPACK FOOTER //
// src/pages/store/GoPro.vue